import type { BoardPower, PowerKind } from "../data/model";
import { flight as F, motor as M, powers as P } from "../data/tuning";
import { defaultAbilities, type Abilities, type Motor } from "./Motor";

export interface ActivePower {
  kind: PowerKind;
  left: number;
  total: number;
}

export type PowerLevels = Record<"magnet" | "x2" | "sneakers" | "jetpack", number>;

export interface PowerEvents {
  toast(message: string): void;
  cue(name: string): void;
  /** mystery power-up results that are not timers */
  bonusMultiplier(amount: number): void;
  grantBoard(): void;
}

export type MysteryResult = "plusMultiplier" | "doubleCoins" | "doubleJump" | "randomBoard";

/**
 * Timers and effects of the power-ups and the hover board, and the abilities they lend the motor. The board is the
 * original's auto-revive: while it is under you a crash breaks the board instead of ending the run. The plain board
 * does only that (DefaultHoverboardPowerConfig: the save + visuals, 30 s, 5 s cooldown); a board's own powers ride
 * along for the same 30 s (MainHoverboardSystem.AddHoverboardPower adds them to the board's power).
 */
export class PowerSystem {
  readonly active = new Map<PowerKind, ActivePower>();
  boardLeft = 0;
  boardCooldown = 0;
  /** the mystery power-up's board: one of the run's drawn boards, ridden for 15 s (RandomBoardPowerConfig) */
  randomBoardLeft = 0;
  shieldActive = false;
  boosterBonus = 0;
  boostersUsed = 0;
  doubleCoinsLeft = 0;
  mysteryDoubleJumpLeft = 0;
  private boardPowers: BoardPower[] = [];
  private randomBoardPowers: BoardPower[] = [];
  private randomCandidates: BoardPower[][] | null = null;
  private randomBoards: BoardPower[][] = [];
  private randomBoardIndex = 0;
  /** a speed board's share of its speed addition, 0…1: grows over 0.5 s when it starts, fades over 0.5 s when it ends */
  private speedShare = 0;
  private speedAddition: readonly [number, number] | null = null;
  private flightEnd: ((kind: "jetpack" | "headstart") => void) | null = null;

  constructor(public levels: PowerLevels = { magnet: 0, x2: 0, sneakers: 0, jetpack: 0 }, private random: () => number = Math.random) {}

  get flying(): "jetpack" | "headstart" | null {
    return this.active.has("jetpack") ? "jetpack" : this.active.has("headstart") ? "headstart" : null;
  }

  /** seconds of flight left (thrusters or headstart), 0 when not flying */
  get flightLeft(): number {
    return Math.max(this.active.get("jetpack")?.left ?? 0, this.active.get("headstart")?.left ?? 0);
  }

  get doubleScore(): boolean {
    return this.active.has("x2");
  }

  get boardActive(): boolean {
    return this.boardLeft > 0 || this.randomBoardLeft > 0;
  }

  /** the powers of whatever board is under you right now (yours and/or the mystery one) */
  get ridingPowers(): BoardPower[] {
    if (this.boardLeft > 0 && this.randomBoardLeft > 0) return [...this.boardPowers, ...this.randomBoardPowers];
    return this.boardLeft > 0 ? this.boardPowers : this.randomBoardLeft > 0 ? this.randomBoardPowers : [];
  }

  /** The coin pull while airborne (the original's jump magnet, 40 m/s): super springs, and the Super Jump and Smooth Drift boards. */
  get jumpMagnet(): boolean {
    if (this.active.has("sneakers")) return true;
    return this.ridingPowers.some((p) => p === "superJump" || p === "smoothDrift" || p === "glider");
  }

  setBoardPowers(powers: BoardPower[]): void {
    this.boardPowers = powers;
  }

  /**
   * The boards the mystery power-up can hand you. The original draws 2 random store boards per run
   * (BoardsManager.GetRandomBoardInStore(2)) and hands them out in turn; ours draws them at the first mystery board,
   * so runs without one use the random numbers exactly as before.
   */
  setRandomBoards(candidates: BoardPower[][]): void {
    this.randomCandidates = candidates;
    this.randomBoards = [];
    this.randomBoardIndex = 0;
  }

  onFlightEnd(handler: (kind: "jetpack" | "headstart") => void): void {
    this.flightEnd = handler;
  }

  seconds(kind: "magnet" | "x2" | "sneakers" | "jetpack"): number {
    const table = P.seconds[kind];
    return table[Math.min(table.length - 1, Math.max(0, this.levels[kind]))];
  }

  /** Picks up a power: starts its timer and effect. */
  activate(kind: PowerKind, motor: Motor, events: PowerEvents): void {
    switch (kind) {
      case "magnet":
        this.start("magnet", this.seconds("magnet"));
        events.toast("MAGNET · SOL COMES TO YOU");
        break;
      case "x2":
        this.start("x2", this.seconds("x2"));
        events.toast("2X SCORE");
        break;
      case "sneakers":
        this.start("sneakers", this.seconds("sneakers"));
        events.toast("SUPER SPRINGS · HOP HIGHER");
        break;
      case "jetpack":
        this.active.delete("headstart");                          // one flight at a time: the newest one flies
        this.start("jetpack", this.seconds("jetpack"));
        motor.startFlight({ height: F.jetpackHeight, speedMultiplier: F.jetpackSpeedMultiplier, climbSeconds: F.jetpackClimbSeconds, kind: "jetpack" });
        events.toast("THRUSTERS · FLY");
        break;
      case "headstart":
        this.active.delete("jetpack");
        this.start("headstart", F.headstartSeconds);
        motor.startFlight({ height: F.headstartHeight, speedMultiplier: 1, fixedSpeed: F.headstartSpeed[0], climbSeconds: 0.6, kind: "headstart" });
        break;
      case "pogo":
        motor.pogo();
        events.toast("MEGA HOP");
        break;
      case "mystery": {
        const result = this.resolveMystery(motor, events);
        events.toast(result === "plusMultiplier" ? "+10 MULTIPLIER" : result === "doubleCoins" ? "DOUBLE SOL" : result === "doubleJump" ? "DOUBLE HOP" : "RANDOM BOARD");
        break;
      }
      case "booster":
        if (this.boostersUsed < 3) {
          const bonus = P.boosterBonus[Math.floor(this.random() * P.boosterBonus.length)];
          this.boosterBonus += bonus;
          this.boostersUsed += 1;
          events.toast(`SCORE BOOSTER +${bonus}`);
        }
        break;
      case "board":
        events.grantBoard();
        events.toast("+1 HOVER BOARD");
        break;
      case "shield":
        this.shieldActive = true;
        events.toast("SHIELD UP");
        break;
    }
    events.cue("power");
  }

  resolveMystery(motor: Motor, events: PowerEvents): MysteryResult {
    const options: MysteryResult[] = ["plusMultiplier", "doubleCoins", "doubleJump", "randomBoard"];
    const result = options[Math.floor(this.random() * options.length)];
    if (result === "plusMultiplier") events.bonusMultiplier(P.mysteryPlusMultiplier);
    else if (result === "doubleCoins") this.doubleCoinsLeft = P.doubleCoinsSeconds;
    else if (result === "doubleJump") this.mysteryDoubleJumpLeft = P.mysteryDoubleJumpSeconds;
    else this.rideRandomBoard();
    void motor;
    return result;
  }

  /** The mystery board: the next of the run's drawn boards, 15 s, with its powers and the crash save (no inventory used). */
  rideRandomBoard(): void {
    if (this.randomBoardLeft > 0) return;              // already on one: the original adds no second (TryGetPower)
    if (!this.randomBoards.length) {
      const pool = this.randomCandidates?.length ? [...this.randomCandidates] : [this.boardPowers];
      while (this.randomBoards.length < P.randomBoardPreloads && pool.length) {
        this.randomBoards.push(pool.splice(Math.floor(this.random() * pool.length), 1)[0]);
      }
    }
    this.randomBoardPowers = this.randomBoards[this.randomBoardIndex % this.randomBoards.length];
    this.randomBoardIndex += 1;
    this.randomBoardLeft = P.randomBoardSeconds;
  }

  /**
   * The flight's timer follows the way left to its landing (the original rescales the power's timer to end there): `left`
   * seconds to go. `rescale` (the landing just became known): the bar keeps what has drained and spreads the rest.
   */
  setFlightLeft(left: number, rescale: boolean): void {
    const kind = this.flying;
    const power = kind ? this.active.get(kind) : undefined;
    if (!power) return;
    if (rescale) power.total = Math.max(0.001, power.total - power.left + left);
    power.left = left;
  }

  /** The flight reached its landing: it ends now (the motor falls, the session hears of it). */
  endFlight(motor: Motor): void {
    const kind = this.flying;
    if (!kind) return;
    this.active.delete(kind);
    motor.endFlight();
    this.flightEnd?.(kind);
  }

  /** Double tap: ride the board (30 s). Returns false when there is no board, one is out (yours or a mystery one), or it is cooling down (5 s). */
  useBoard(boards: number): boolean {
    if (boards <= 0 || this.boardLeft > 0 || this.randomBoardLeft > 0 || this.boardCooldown > 0) return false;
    this.boardLeft = P.boardSeconds;
    return true;
  }

  /** A crash while protected: the board breaks (yours first, then a mystery one), or the shield pops. Returns what saved you. */
  absorbCrash(): "board" | "shield" | null {
    if (this.boardLeft > 0) {
      this.boardLeft = 0;
      this.boardCooldown = P.boardCooldownSeconds;
      return "board";
    }
    if (this.randomBoardLeft > 0) {
      this.randomBoardLeft = 0;                        // RandomBoardPowerConfig has no cooldown
      return "board";
    }
    if (this.shieldActive) {
      this.shieldActive = false;
      return "shield";
    }
    return null;
  }

  update(dt: number, motor: Motor): void {
    for (const [kind, power] of this.active) {
      power.left -= dt;
      if (power.left <= 0) {
        this.active.delete(kind);
        if (kind === "jetpack" || kind === "headstart") {
          motor.endFlight();
          this.flightEnd?.(kind);
        }
      }
    }
    if (this.boardLeft > 0) {
      this.boardLeft = Math.max(0, this.boardLeft - dt);
      if (this.boardLeft === 0) this.boardCooldown = P.boardCooldownSeconds;
    } else if (this.boardCooldown > 0) this.boardCooldown = Math.max(0, this.boardCooldown - dt);
    if (this.randomBoardLeft > 0) this.randomBoardLeft = Math.max(0, this.randomBoardLeft - dt);
    // a speed board's addition eases in and out (SpeedupPowerEffect.AdjustSpeed: linear over 0.5 s)
    const speedPower = this.ridingPowers.includes("superSpeed") ? M.speedAdditions.superSpeed : this.ridingPowers.includes("speed") ? M.speedAdditions.speed : null;
    if (speedPower) this.speedAddition = speedPower;
    this.speedShare = speedPower ? Math.min(1, this.speedShare + dt / M.speedEaseSeconds) : Math.max(0, this.speedShare - dt / M.speedEaseSeconds);
    if (this.doubleCoinsLeft > 0) this.doubleCoinsLeft = Math.max(0, this.doubleCoinsLeft - dt);
    if (this.mysteryDoubleJumpLeft > 0) this.mysteryDoubleJumpLeft = Math.max(0, this.mysteryDoubleJumpLeft - dt);
    motor.abilities = this.abilities();
  }

  /**
   * What the motor can do right now: the powers of the board under you, the springs while they last. Each board power
   * is the original's effect (MainHoverboardSystemData): Super Jump = the high jump (×2) + jump magnet; Stay Low = the
   * shrink; Speed / Super Speed = +25/+10 or +50/+20 u/s on the start/end speed; Double Jump = a jump limit of 2;
   * Smooth Drift = the glider + jump magnet; Zap Sideways = 0.18 s lane changes, no trips and no corner hits.
   */
  abilities(): Abilities {
    const a = defaultAbilities();
    const powers = this.ridingPowers;
    const highJump = this.active.has("sneakers") || powers.includes("superJump");
    if (highJump) {
      a.jumpHeightMultiplier = M.highJumpMultiplier;
      a.airJumpHeight = M.superJumpAirJump * M.highJumpMultiplier;   // AirJumpHeight = the ability's 9 u × its multiplier
    }
    if (powers.includes("doubleJump") || this.mysteryDoubleJumpLeft > 0) a.airJumps = 1;
    if (powers.includes("stayLow")) a.stayLow = true;
    if (powers.includes("smoothDrift") || powers.includes("glider")) a.glide = true;
    if (powers.includes("zapSideways")) {
      a.laneChangeSeconds = M.laneChangeSecondsFast;
      a.noLowerCollision = true;
      a.noCornerCollision = true;
    }
    if (this.speedShare > 0 && this.speedAddition) {
      a.speedAddition = [this.speedAddition[0] * this.speedShare, this.speedAddition[1] * this.speedShare];
    }
    return a;
  }

  /** The run's multiplier additions from powers: boosters + mystery bonus (handled by the session) — x2 doubles the total. */
  multiplierBonus(): number {
    return this.boosterBonus;
  }

  endRun(): void {
    this.active.clear();
    this.boardLeft = 0;
    this.randomBoardLeft = 0;
    this.speedShare = 0;
  }

  private start(kind: PowerKind, seconds: number): void {
    this.active.set(kind, { kind, left: seconds, total: seconds });
  }
}
